import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PendingIcon from '@mui/icons-material/Pending'
import Timeline from '@mui/lab/Timeline'
import { TimelineItem, TimelineSeparator, TimelineDot, TimelineConnector, TimelineContent } from '@mui/lab'
import { Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { VehicleClusterTimelineDto, VehicleDto } from 'dtos'
import { formatDateTime } from 'utils'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { useEffect, useState } from 'react'

interface ClusterTimelineProps {
  vehicle: VehicleDto
}

export default function ClusterTimeline({ vehicle }: ClusterTimelineProps) {
  const [isGettingClusterTimeline, setIsGettingClusterTimeline] = useState<boolean>(false)
  const [clusterTimeline, setClusterTimeline] = useState<VehicleClusterTimelineDto>()

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingClusterTimeline(true)
      controllers
        .getClusterTimeline(vehicle.vehicleGuid!)
        .then(response => {
          setClusterTimeline(response.value)
        })
        .finally(() => {
          setIsGettingClusterTimeline(false)
        })
    }
  }, [vehicle])

  const clusterConversionType = vehicle.clusterInformation?.clusterConversionInHouseOrThirdParty
  const additionalProgrammingType = vehicle.clusterInformation?.additionalProgrammingInHouseOrThirdParty

  const isConversionInHouse =
    clusterConversionType === 'In-House' || Boolean(clusterTimeline?.clusterConversionStartedInHouseDateTime)
  const isProgrammingInHouse =
    additionalProgrammingType === 'In-House' || Boolean(clusterTimeline?.additionalProgrammingInHouseStartedDateTime)

  const timelineSteps = [
    { label: 'Created', date: clusterTimeline?.createdDateTime, isRelevant: () => true },
    { label: 'Vehicle Check-In Completed', date: clusterTimeline?.vehicleCheckInDateTime, isRelevant: () => true },
    {
      label: 'Cluster Removal Started',
      date: clusterTimeline?.clusterRemovalStartedDateTime,
      person: clusterTimeline?.clusterRemovalStartedByPerson,
      isRelevant: () => true
    },
    {
      label: 'Cluster Pulled',
      date: clusterTimeline?.clusterPulledDateTime,
      person: clusterTimeline?.clusterPulledByPerson,
      isRelevant: () => true
    },
    {
      label: 'Cluster Conversion In-House Started',
      date: clusterTimeline?.clusterConversionStartedInHouseDateTime,
      person: clusterTimeline?.clusterConversionStartedInHouseByPerson,
      isRelevant: () => isConversionInHouse
    },
    {
      label: 'Cluster Delivered To 3rd Party Conversion Company',
      date: vehicle.clusterShipper?.dropOffDate,
      isRelevant: () => !isConversionInHouse
    },
    {
      label: 'Cluster Conversion Completed',
      date: clusterTimeline?.clusterConversionCompletedDateTime,
      person: clusterTimeline?.clusterConversionCompletedByPerson,
      isRelevant: () => true
    },
    {
      label: 'Additional Programming In-House Started',
      date: clusterTimeline?.additionalProgrammingInHouseStartedDateTime,
      person: clusterTimeline?.additionalProgrammingInHouseStartedByPerson,
      isRelevant: () => isProgrammingInHouse
    },
    {
      label: 'Cluster Delivered To 3rd Party Additional Programming Company',
      date: clusterTimeline?.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyDateTime,
      person: clusterTimeline?.clusterDeliveredToThirdPartyAdditionalProgrammingCompanyByPerson,
      isRelevant: () => !isProgrammingInHouse
    },
    {
      label: 'Additional Programming Complete',
      date: clusterTimeline?.additionalProgrammingCompletedDateTime,
      person: clusterTimeline?.additionalProgrammingCompletedByPerson,
      isRelevant: () => true
    },
    {
      label: 'Cluster Reinstallation Complete',
      date: clusterTimeline?.clusterReinstallationCompletedDateTime,
      person: clusterTimeline?.clusterReinstallationCompletedByPerson,
      isRelevant: () => true
    }
  ]

  let allCompleted = true

  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h5'>
              Cluster Timeline
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {isGettingClusterTimeline && (
            <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
              <Grid>
                <CircularProgress color='primary' size={50} />
              </Grid>
            </Grid>
          )}

          {!isGettingClusterTimeline && (
            <>
              <Timeline position='alternate'>
                {timelineSteps.map(({ label, date, person, isRelevant }, index) => {
                  const relevant = isRelevant()
                  const isCompleted = date !== null && date !== undefined
                  if (!isCompleted) allCompleted = false

                  return (
                    <TimelineItem key={index}>
                      <TimelineSeparator>
                        <TimelineDot
                          sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          color={relevant && isCompleted ? 'primary' : 'grey'}
                        >
                          {relevant && isCompleted ? (
                            <CheckCircleIcon sx={{ fontSize: 32 }} />
                          ) : (
                            <PendingIcon sx={{ fontSize: 32 }} />
                          )}
                        </TimelineDot>
                        {index < timelineSteps.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent>
                        <Typography variant='body1' color='textSecondary' fontWeight='bold'>
                          {label}
                        </Typography>
                        <Typography variant='body1' color='textSecondary' fontWeight='regular'>
                          {isCompleted ? (
                            <>
                              {formatDateTime(date)}

                              {person && (
                                <>
                                  <br />
                                  by {person.firstName} {person.lastName}
                                </>
                              )}
                            </>
                          ) : (
                            'TBD'
                          )}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>
                  )
                })}
              </Timeline>
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          <GreenText variant='outlined'>
            {`**Created:**

Step “Created” shows the date and time the vehicle record was created in VTS2.

**Vehicle Check-In Completed:**

Displays the date and time the first “Vehicle Check-In” image was associated with the vehicle.

If incomplete, it will display “TBD”.

**Cluster Removal Started:**

Displays the date and time “Start Cluster Removal” was pressed on the Needs Cluster Removed – Scan Cluster Barcode screen, along with
the name of the user.

If incomplete, it will display “TBD”.

**Cluster Pulled:**

Displays the date and time “Submit” was pressed on the Needs Cluster Removed – Pulled Cluster Review screen, along with the name of the
user.

If incomplete, it will display “TBD”.

**Cluster Conversion Started In-House:**

Displays the date and time “Start Cluster Conversion” was pressed on the Needs Cluster Conversion In-House – Scan Cluster Barcode
screen, along with the name of the user.

If incomplete, it will display “TBD”.

**Cluster Delivered To 3rd Party Conversion Company:**

Displays the date and time the “3rd Party Conversion Delivery” image was associated with the vehicle.

This can occur in two ways:
   - A user drops off a bin of clusters at a 3rd Party Conversion Company.
   - A user with VTS2 Access “Cluster Admin” manually uploads this image to the Cluster Shipper, which associates it with all vehicles in the Cluster Shipper.

If incomplete, it will display “TBD”.

**Cluster Conversion Completed:**

Displays the date and time “Cluster Conversion Completed” was pressed on either:
   - The Waiting on 3rd Party Conversion Company – Scan Cluster Barcode screen.
   - The Needs Cluster Conversion In-House – Scan Cluster Barcode screen.

Includes the name of the user.

If incomplete, it will display “TBD”.

**Additional Programming In-House Started:**

Displays the date and time “Start Additional Programming” was pressed on the Needs In-House Additional Programming – Scan Cluster
Barcode screen, along with the name of the user.

If incomplete, it will display “TBD”.

**Cluster Delivered To 3rd Party Additional Programming Company:**

Displays the date and time “Deliver to 3rd Party Additional Programmer” was pressed on the Needs 3rd Party Additional Programming –
Scan Programming Barcode screen, along with the name of the user.

If incomplete, it will display “TBD”.

**Additional Programming Complete:**

Displays the date and time “Additional Programming Completed” was pressed on either:
   - The Needs In-House Additional Programming – Scan Cluster Barcode screen.
   - The Waiting on 3rd Party Additional Programming – Scan Cluster Barcode screen.

Includes the name of the user.

If incomplete, it will display “TBD”.

**Cluster Reinstallation Complete:**

Displays the date and time “Submit” was pressed on the Needs Cluster Reinstalled – Cluster Reinstall Review screen, along with the name of
the user.

If incomplete, it will display “TBD”`}
          </GreenText>
        </Grid>
      </CardContent>
    </Card>
  )
}
